import type { GetCurrentMemberFn, GetTeamMemberFn } from '@/features/iam'
import { type JobStore } from '@/features/jobs/JobStore'
import type { DraftJobStore } from '@/features/jobs/drafting/DraftJobStore'
import { action, computed, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import type { Id } from '@/infra/id'
import type { FetchCouponByCodeFn } from '@/features/billing'
import { type ApplyCouponAsAdminDialogViewModelFactory } from '@/features/jobs/ApplyCouponAsAdminDialog/ApplyCouponAsAdminDialogViewModel'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import type { NavigateFn } from '@/RootStore'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type {
  AddCardBillingMethodFn,
  FetchBillingProfileFn,
  GetStripePublishableKeyFn,
} from '@/features/billing'
import type { BillingProfile } from '@/features/billing/profile/BillingProfile'
import { type BillingMethod } from '@/features/billing/profile/BillingMethod'
import { type Workspace } from '@/features/iam/workspaces/Workspace'
import { CardPaymentMethodFormViewModel } from '@/features/jobs/drafting/checkout/CardPaymentMethodFormViewModel'
import { loadStripe } from '@stripe/stripe-js'
import {
  type FetchCheckoutLegendVersionFn,
  JobCheckoutViewModel,
} from './JobCheckoutViewModel'
import type { Observable } from 'rxjs'
import { tapAction } from '@/utils/mobx-util'
import { ViewModelList } from '@/infra/view-model-list'

/**
 * A payment method the client can pay with.
 */
export class BillingMethodViewModel {
  constructor(
    private readonly _billingMethod: BillingMethod,
    private readonly _getWorkspace: () => Workspace,
    private readonly _isSelected: (billingMethod: BillingMethod) => boolean,
    private readonly _onSelect: (billingMethod: BillingMethod) => void,
  ) {}

  /**
   * Whether this method is the selected one.
   */
  @computed
  get selected() {
    return this._isSelected(this._billingMethod)
  }

  /**
   * The billing method ID.
   */
  @computed
  get id() {
    return this._billingMethod.id
  }

  /**
   * The card brand.
   */
  @computed
  get brand() {
    return this._billingMethod.stripeCard?.brand ?? ''
  }

  /**
   * The last 4 digits of the card.
   */
  @computed
  get last4() {
    return this._billingMethod.stripeCard?.last4 ?? ''
  }

  /**
   * The card expiry, as `month/year`.
   */
  @computed
  get expires() {
    return `${this._billingMethod.stripeCard?.expMonth ?? 0}/${this._billingMethod.stripeCard?.expYear ?? 0}`
  }

  /**
   * The title of a workspace credit method.
   */
  @computed
  get title() {
    return `${this._getWorkspace().name} Credit`
  }

  /**
   * The billing method type.
   */
  @computed
  get type() {
    return this._billingMethod.type.type
  }

  /**
   * Selects this method, or clears the selection when it is selected.
   */
  @action.bound
  toggleSelected() {
    this._onSelect(this._billingMethod)
  }
}

/**
 * Checkout of one or more jobs.
 */
export class CheckoutViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * The existing payment method to pay with, if any.
   */
  @observable accessor selectedPaymentMethod: BillingMethod | null = null

  /**
   * The jobs being checked out.
   */
  @observable accessor jobCheckoutVms: JobCheckoutViewModel[] = []

  /**
   * View Model for card payment form.
   */
  @observable
  accessor cardPaymentMethodFormVm: CardPaymentMethodFormViewModel | null = null

  /**
   * The loaded billing profiles.
   */
  @observable private accessor _billingProfiles: BillingProfile[] = []

  /**
   * View models for the billing profile's payment methods.
   */
  private readonly _paymentMethodVms

  constructor(
    private readonly _draftJobStore: DraftJobStore,
    private readonly _jobStore: JobStore,
    private readonly _fetchLegendVersion: FetchCheckoutLegendVersionFn,
    private readonly _createApplyCouponAsAdminViewModel: ApplyCouponAsAdminDialogViewModelFactory,
    private readonly _fetchCouponByCode: FetchCouponByCodeFn,
    private readonly _billingProfiles$: Observable<BillingProfile[]>,
    private readonly _fetchBillingProfile: FetchBillingProfileFn,
    private readonly _getStripePublishableKey: GetStripePublishableKeyFn,
    private readonly _addCardBillingMethod: AddCardBillingMethodFn,
    private readonly _navigate: NavigateFn,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _getTeamMember: GetTeamMemberFn,
  ) {
    this._paymentMethodVms = new ViewModelList<
      BillingMethod,
      BillingMethodViewModel
    >({
      models: () => this._billingProfile?.billingMethods.slice() ?? [],
      create: (billingMethod) =>
        new BillingMethodViewModel(
          billingMethod,
          () => this.workspace,
          (bm) => bm.id === this.selectedPaymentMethod?.id,
          this.setSelectedPaymentMethod,
        ),
    })
    this._lifecycle.manage(this._paymentMethodVms)
    this._lifecycle.manage(
      _billingProfiles$.pipe(
        tapAction((billingProfiles) => {
          this._billingProfiles = billingProfiles
        }),
      ),
    )
  }

  /**
   * Loads the information necessary for checking out the jobs.
   */
  fetch = task(
    async ({ jobIds, getLinkWithoutJob, signal }: CheckoutLoadOptions) => {
      const removeJob = getLinkWithoutJob
        ? (id: Id) => this._navigate(getLinkWithoutJob(id))
        : null

      runInAction(() => {
        this.jobCheckoutVms.forEach((vm) => this._lifecycle.remove(vm))
        this.jobCheckoutVms = jobIds.map(
          (id) =>
            new JobCheckoutViewModel(
              id,
              this._draftJobStore,
              this._jobStore,
              this._fetchLegendVersion,
              this._createApplyCouponAsAdminViewModel,
              this._fetchCouponByCode,
              this._navigate,
              this._newAlert,
              this._createAlertErrorHandler,
              this._getCurrentMember,
              this._getTeamMember,
              removeJob,
            ),
        )
        this.jobCheckoutVms.forEach((vm) => this._lifecycle.manage(vm))
      })

      if (this.jobCheckoutVms.length === 0) {
        return
      }

      await Promise.all(
        this.jobCheckoutVms.map((vm) => vm.fetchJobInfo(signal)),
      )
      await this.fetchPaymentMethods(signal)

      const member = this.member
      if (member) {
        const stripe = await this._loadStripe(signal)
        runInAction(() => {
          if (this.cardPaymentMethodFormVm) {
            this._lifecycle.remove(this.cardPaymentMethodFormVm)
          }
          this.cardPaymentMethodFormVm = new CardPaymentMethodFormViewModel(
            member,
            () => this.pay(),
            this._addCardBillingMethod,
            stripe,
            () => this.selectedPaymentMethod !== null,
          )
          this._lifecycle.manage(this.cardPaymentMethodFormVm)
        })
      }
    },
  )

  /**
   * Whether every job has been submitted.
   */
  @computed
  get checkoutCompleted() {
    return this.jobCheckoutVms.every((vm) => vm.checkoutCompleted)
  }

  /**
   * The member the jobs are checked out for.
   */
  @computed
  get member() {
    return this.jobCheckoutVms[0]?.job?.championClient?.member
  }

  /**
   * The workspace of the jobs.
   */
  @computed
  get workspace() {
    return this.jobCheckoutVms[0]?.job?.workspace
  }

  /**
   * List of existing payment methods, `null` when the client has no billing
   * profile.
   */
  @computed
  get paymentMethods() {
    return this._billingProfile ? this._paymentMethodVms.items : null
  }

  /**
   * Whether or not the client has payment methods.
   */
  @computed
  get hasPaymentMethods() {
    return Boolean(this.paymentMethods && this.paymentMethods.length > 0)
  }

  /**
   * The total amount remaining to be paid.
   */
  @computed
  get total() {
    return this.jobCheckoutVms.reduce((acc, vm) => {
      return vm.checkoutCompleted ? acc + 0 : acc + vm.amountDueNow
    }, 0)
  }

  /**
   * The billing profile of the client.
   *
   * @private
   */
  @computed
  private get _billingProfile() {
    return this._billingProfiles.find(
      (p) =>
        p.userId === this.member?.user.id && p.workspace === this.workspace,
    )
  }

  /**
   * Fetch existing payment methods.
   */
  @action.bound
  async fetchPaymentMethods(signal?: AbortSignal) {
    if (this.member) {
      await this._fetchBillingProfile(this.member)
      signal?.throwIfAborted()
    }
  }

  /**
   * Sets the selected payment method.
   * @param billingMethod
   */
  @action.bound
  setSelectedPaymentMethod(billingMethod: BillingMethod) {
    this.selectedPaymentMethod =
      billingMethod === this.selectedPaymentMethod ? null : billingMethod
  }

  /**
   * Submit the job with the selected payment method.
   */
  @action.bound
  async pay() {
    const alert = this._newAlert()
      .withState('pending')
      .saying('Submitting jobs', 'Hang tight!')
      .show()
      .autoDismiss()

    await this._submitJobs()
      .catch((err: unknown) => {
        alert.dismiss()
        throw err
      })
      .catch(this._createAlertErrorHandler('Submitting the jobs failed'))

    alert
      .withState('success')
      .saying('Success!', 'All jobs have been submitted!')
      .autoDismiss()
  }

  /**
   * Clears the selected payment method.
   */
  @action.bound
  clearSelectedPaymentMethod() {
    this.selectedPaymentMethod = null
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
    this.clearSelectedPaymentMethod()
  }

  /**
   * Loads the stripe instance.
   */
  private async _loadStripe(signal?: AbortSignal) {
    const key = this._getStripePublishableKey()
    if (!key) {
      throw new Error(
        'There is a missing Billing configuration. Please contact an administrator.',
      )
    }

    const stripe = await loadStripe(key)
    signal?.throwIfAborted()
    return stripe
  }

  /**
   * Submits every job, paying with the selected payment method, or with one
   * added through the card form when none is selected.
   */
  private async _submitJobs() {
    const paymentMethod =
      this.selectedPaymentMethod ??
      (await this.cardPaymentMethodFormVm?.savePaymentMethod()) ??
      null

    await Promise.all(this.jobCheckoutVms.map((vm) => vm.pay(paymentMethod)))
  }
}

/**
 * Options for {@link CheckoutViewModel.fetch}.
 */
export interface CheckoutLoadOptions {
  jobIds: Id[]
  /**
   * Builds the link to the checkout without the given job, `null` when jobs
   * cannot be removed from this checkout.
   */
  getLinkWithoutJob: ((jobId: Id) => string) | null
  signal?: AbortSignal
}
